// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { ErrorScreen } from './error-screen.js';

/**
 * COB-548 (GF-3) — full-screen replacement shown when the backend refuses
 * the whole `/api` surface for the viewer's region (`451` with
 * `tier: "prohibited"`). Replaces the entire SessionProvider subtree, the
 * same treatment `BootstrapUnreachable` gets, so no half-rendered home
 * page peeks through.
 *
 * Deliberately offers no retry: unlike an unreachable backend, nothing the
 * user can do from here changes the answer, and a "Try again" button would
 * invite them to sit on it. The primary action reloads instead, which is
 * the honest affordance for "I have travelled / this looks wrong".
 *
 * COB-586 — `invited` re-points the copy at the shared-vault invitation
 * the viewer was actually trying to open. Because this screen replaces the
 * whole subtree, the accept page's own error state never gets to render,
 * so without the flag an invitee who followed a claim link would be told
 * only that "Cobault isn't available" and left to guess what became of the
 * invitation. The distinction the copy has to carry is the one GF-3 draws:
 * `POST /shared-vault` (open a NEW one) is acquisition-only, while
 * `resolve-token` / `accept` / `GET /shared-vault/{id}` are servicing — so
 * "you can't open a new one here" and "this one can't be opened here" are
 * genuinely different refusals and must not share a sentence.
 */
export function GeoRestricted({ invited = false }: { invited?: boolean }) {
  return (
    <ErrorScreen
      title={
        invited
          ? "This invitation can't be opened in your region just yet"
          : "Cobault isn't available in your region just yet"
      }
      body={
        invited ? (
          <>
            <p>
              We operate in a limited set of jurisdictions and are expanding
              carefully. We can't open shared-vault invitations from where
              you're connecting today.
            </p>
            <p>
              Your invitation hasn't been used — open the link again from a
              region where Cobault is available. If you're travelling, or on
              a VPN, reconnect to your usual network and reload.
            </p>
          </>
        ) : (
          <>
            <p>
              We operate in a limited set of jurisdictions and are expanding
              carefully. We're not open where you're connecting from today.
            </p>
            <p>
              If you believe this is a mistake — you're travelling, or on a
              VPN — reload once you're back on your usual connection, or get
              in touch with support.
            </p>
          </>
        )
      }
      primaryLabel="Reload"
      onPrimary={() => window.location.reload()}
    />
  );
}

/**
 * COB-548 (GF-3) — the servicing-tier banner. The user's country is one we
 * service but do not market into: every existing vault stays fully
 * usable — view, sign, withdraw — and only the acquisition surface (new
 * accounts, offers, new vaults) is refused.
 *
 * A banner rather than a gate, because saying "unavailable" to someone
 * whose Bitcoin is sitting in one of our vaults would be both false and
 * alarming. Rendered in `Layout` beside the KYC and email-verify banners.
 */
export function GeoServicingBanner() {
  // Reuses the `kyc-banner` classes rather than minting new ones: this is
  // the same slot, the same shape, and the same advisory weight, and a
  // bespoke class here would be one more rule to keep in step.
  return (
    <div class="kyc-banner" role="status">
      <div class="kyc-banner-inner">
        <div class="kyc-banner-text">
          <strong>New vaults aren't available in your region just yet.</strong>{' '}
          {GEO_SERVICING_NOTE}
        </div>
      </div>
    </div>
  );
}

/** The half of the servicing-banner copy that carries the reassurance.
 *  Exported (and pinned in the tests) because it is the sentence that
 *  stops an existing user believing their Bitcoin is stranded — a
 *  reworded "improvement" should fail a test and be re-ratified, not
 *  ship. */
export const GEO_SERVICING_NOTE =
  'Your existing vaults stay fully accessible — you can view, sign, and withdraw as usual.';
